import { DynamicTableSVG } from "../../shapes/DynamicFurnitureShape/DynamicFurnitureShape";
import { IconFromSet } from "@gettactic/components";
import React from "react";
import { Shape } from "../../../../types";
import { getEffectiveSpotAppearance } from "../../../../utils/spots";
import {
  DEFAULT_NODE_RECT_COLOR,
  DEFAULT_WALL_COLOR,
  DEFAULT_ZONE_BORDER_COLOR
} from "../../defines";

const BOX = 20;
const PAD = 2;
const INNER = BOX - PAD * 2;

/** Fit a width/height into the preview box preserving aspect ratio. */
const fitRect = (width: number, height: number) => {
  const w = Math.max(width, 1);
  const h = Math.max(height, 1);
  const scale = INNER / Math.max(w, h);
  const fw = Math.max(w * scale, 3);
  const fh = Math.max(h * scale, 3);
  return {
    x: (BOX - fw) / 2,
    y: (BOX - fh) / 2,
    width: fw,
    height: fh
  };
};

/**
 * A tiny geometry-accurate thumbnail of a shape for the layers list:
 * real fill/stroke colors and rough proportions, rendered as plain SVG
 * (no Konva involved).
 */
export const LayerPreview: React.FC<{ shape: Shape }> = ({ shape }) => {
  switch (shape.type) {
    case "dynamic-furniture": return <span style={{width:20,height:20}}><DynamicTableSVG layout={shape.layout} color={shape.color}/></span>;
    case "rectangle": {
      const r = fitRect(shape.width, shape.height);
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <rect
            {...r}
            rx={shape.cornerRadius ? 2 : 0.5}
            fill={
              shape.noFill ? "none" : shape.color || DEFAULT_NODE_RECT_COLOR
            }
            stroke={
              shape.stroke ||
              (shape.noFill ? shape.color : undefined) ||
              "#b3b3bb"
            }
            strokeWidth={1}
          />
        </svg>
      );
    }
    case "circle": {
      const r = fitRect(shape.width, shape.height);
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <ellipse
            cx={BOX / 2}
            cy={BOX / 2}
            rx={r.width / 2}
            ry={r.height / 2}
            fill={
              shape.noFill ? "none" : shape.color || DEFAULT_NODE_RECT_COLOR
            }
            stroke={shape.stroke || "#b3b3bb"}
            strokeWidth={1}
          />
        </svg>
      );
    }
    case "zone": {
      const r = fitRect(shape.width, shape.height);
      const border = shape.borderColor || DEFAULT_ZONE_BORDER_COLOR;
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <rect
            {...r}
            rx={1.5}
            fill={border}
            fillOpacity={0.15}
            stroke={border}
            strokeWidth={1}
            strokeDasharray="2 1.5"
          />
        </svg>
      );
    }
    case "wall": {
      // Normalize the wall's direction into the box.
      const dx = shape.x2 - shape.x;
      const dy = shape.y2 - shape.y;
      const len = Math.max(Math.hypot(dx, dy), 1);
      const half = INNER / 2;
      const ux = (dx / len) * half;
      const uy = (dy / len) * half;
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <line
            x1={BOX / 2 - ux}
            y1={BOX / 2 - uy}
            x2={BOX / 2 + ux}
            y2={BOX / 2 + uy}
            stroke={shape.color || DEFAULT_WALL_COLOR}
            strokeWidth={Math.min(Math.max(shape.thickness / 4, 2), 4)}
            strokeLinecap="round"
          />
        </svg>
      );
    }
    case "wall-path": {
      const xs = shape.vertices.map((v) => v.x);
      const ys = shape.vertices.map((v) => v.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const spanX = Math.max(Math.max(...xs) - minX, 1);
      const spanY = Math.max(Math.max(...ys) - minY, 1);
      const scale = INNER / Math.max(spanX, spanY);
      const offX = (BOX - spanX * scale) / 2;
      const offY = (BOX - spanY * scale) / 2;
      const points = shape.vertices
        .map(
          (v) => `${offX + (v.x - minX) * scale},${offY + (v.y - minY) * scale}`
        )
        .join(" ");
      const Tag = shape.closed ? "polygon" : "polyline";
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <Tag
            points={points}
            fill="none"
            stroke={shape.color || DEFAULT_WALL_COLOR}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    }
    case "text":
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <text
            x={BOX / 2}
            y={BOX / 2 + 4.5}
            textAnchor="middle"
            fontSize={13}
            fontWeight={600}
            fill={shape.color || "#55555e"}
            fontFamily="inherit"
          >
            {(shape.text || "T").trim().charAt(0).toUpperCase() || "T"}
          </text>
        </svg>
      );
    case "spot": {
      const fill = shape.color || DEFAULT_NODE_RECT_COLOR;
      const appearance = getEffectiveSpotAppearance(
        shape.spotType,
        shape.spotAppearance
      );
      if (appearance === "meeting_room" || appearance === "circle") {
        return (
          <svg width={BOX} height={BOX} aria-hidden="true">
            <circle
              cx={BOX / 2}
              cy={BOX / 2}
              r={INNER / 2}
              fill={fill}
              stroke="#b3b3bb"
              strokeWidth={1}
            />
          </svg>
        );
      }
      // Desk: table + chair silhouette
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <rect
            x={PAD}
            y={4}
            width={INNER}
            height={9}
            rx={1.5}
            fill={fill}
            stroke="#b3b3bb"
            strokeWidth={1}
          />
          <circle cx={BOX / 2} cy={16} r={2.6} fill="#b3b3bb" />
        </svg>
      );
    }
    case "group": {
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <rect
            x={3}
            y={3}
            width={10}
            height={10}
            rx={1.5}
            fill="none"
            stroke="#9d9da8"
            strokeWidth={1.2}
          />
          <rect
            x={7}
            y={7}
            width={10}
            height={10}
            rx={1.5}
            fill="#e5e6e9"
            stroke="#9d9da8"
            strokeWidth={1.2}
          />
        </svg>
      );
    }
    case "icon":
      return <IconFromSet icon={shape.icon} size={5} />;
    case "furniture":
      return (
        <img
          alt={shape.furniture.name}
          src={shape.furniture.url}
          style={{ width: BOX, height: BOX, objectFit: "contain" }}
        />
      );
    default:
      return (
        <svg width={BOX} height={BOX} aria-hidden="true">
          <rect
            x={PAD}
            y={PAD}
            width={INNER}
            height={INNER}
            rx={1}
            fill="none"
            stroke="#9d9da8"
            strokeWidth={1.2}
          />
        </svg>
      );
  }
};
